/**
 * Confirms reset before posting the native resetAllocation CTA.
 *
 * Belongs here: the confirm copy and the two actions.
 * Does not belong here: postCTA or refetch (the footer does that on confirm).
 */

import { useEffect } from 'react'
import BottomSheetContent from '../../../../../../components/BottomSheetComponents/BottomSheetContent'
import { PrimaryButton } from '../../../../../../components/execution/PrimaryButton'
import { SecondaryButton } from '../../../../../../components/execution/SecondaryButton'
import { EventNames } from '../../../../../../types/misc.types'
import { trackEvent } from '../../../../../../utils/appBridge'
import { PlansBottomSheet } from '../../../analytics/plansEvents'
import { StepRowBadge } from '../../plan-bottom-sheet/shared/steps/StepsToRebalanceAccordionBody'
import RenderHTML from '../../../../../../components/ui-components/RenderHTML'

type ResetStrategyBottomSheetProps = {
  screenName?: string
  onConfirm: () => void
  onDismiss: () => void
}

export function ResetStrategyBottomSheet({
  screenName,
  onConfirm,
  onDismiss
}: ResetStrategyBottomSheetProps) {
  useEffect(() => {
    trackEvent({
      eventName: EventNames.BottomSheetLoad,
      payload: {
        ScreenName: screenName,
        eventId: PlansBottomSheet.ResetStrategy,
        bottomSheetName: PlansBottomSheet.ResetStrategy
      }
    })
  }, [screenName])

  return (
    <BottomSheetContent
      onDismiss={onDismiss}
      logoSvg={<RenderHTML innerHTMLText={RESET_STRATEGY_SHEET_ICON} />}
      heading="Reset strategy"
      bottomSheetHeadingClassName="!mb-3 !font-[500] !leading-[130%]"
      bottomSheetBodyClassName="!pt-0 !pb-4"
      bottomSheetCtaClassName="!px-5 !pt-8"
      bottomSheetCtas={
        <div className="flex flex-col gap-3">
          <PrimaryButton
            label="Confirm reset"
            onClick={onConfirm}
            primaryButtonClassName="!flex !h-14 !items-center !justify-center !rounded-2xl !p-0 !pb-[5px] !text-[16px] !leading-normal"
          />
          <SecondaryButton
            label="Keep my current strategy"
            onClick={onDismiss}
            secondaryButtonClassName="!h-14 !rounded-2xl !border-0 !bg-[#F0F4F7] !p-0 !pb-[5px] !text-[16px] !leading-normal hover:!border-0"
          />
        </div>
      }
    >
      <p className="text-[14px] font-[400] leading-[140%] text-[#6A6E6E]">
        This can&apos;t be undone. Only reset if your goals have changed or this strategy no longer suits you.
      </p>
      <ol className="mt-8 flex list-none flex-col gap-5 p-0">
        {[
          'Your lump sum and SIP plans will be deleted',
          "You'll need to fill in your preferences & risk test again",
          'Your new plan will be ready in 3–5 days'
        ].map((step, stepIndex) => (
          <li key={step} className="flex items-center gap-3">
            <div className='w-[26px]'>
              <StepRowBadge index={stepIndex + 1} />
            </div>
            <span className="text-[14px] font-[500] leading-[140%] text-[#232323]">{step}</span>
          </li>
        ))}
      </ol>
    </BottomSheetContent>
  )
}

export const RESET_STRATEGY_SHEET_ICON = `<svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 40 40" fill="none">
  <path d="M20 4C28.8366 4 36 11.1634 36 20C36 28.8366 28.8366 36 20 36C11.1634 36 4 28.8366 4 20H7.55556C7.55556 26.8729 13.1271 32.4444 20 32.4444C26.8729 32.4444 32.4444 26.8729 32.4444 20C32.4444 13.1271 26.8729 7.55556 20 7.55556C15.7733 7.55556 12.0364 9.66361 9.78645 12.8889H15.5556V16.4444H4V4.88889H7.55556V9.9427C10.4877 6.31877 14.972 4 20 4Z" fill="black" stroke-width="0.75" stroke="white"/>
</svg>`
